import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faCheck } from "@fortawesome/free-solid-svg-icons"
import CustomModal from "../../basics/interface/CustomModal"
import { COLORS } from "../../../utilities/Constants"
import { URL } from "../../../store/actions/depannageActions"

// Correction manuelle du stock physique (bouton « Corriger » de l'onglet
// Stock, chef_atelier). Le backend déplace du restant sur les lots EXISTANTS
// (perte = débit FIFO, récupération = crédit du lot le plus récemment entamé)
// — jamais de création de lot ni de commande. Chaque correction est typée +
// motivée (le type pilote le bloc « Pertes stock » des stats) et notifie le
// boss (stock_modifie).

// Miroir des types serveur : sens -1 = baisse seulement, 1 = hausse
// seulement, 0 = les deux (écart d'inventaire).
const TYPES = [
	{ key: "perte", label: "Perte", sens: -1 },
	{ key: "recuperation", label: "Récupération", sens: 1 },
	{ key: "inventaire", label: "Écart d'inventaire", sens: 0 },
]

// Affichage compact d'un delta flottant (évite 0.7999999).
const fmt = (x) => String(Number(x.toFixed(3)))

const CorrectionModalV2 = ({ article, postState, onCorriger, onClose }) => {
	const token = useSelector((s) => s.navInfo.login.token)

	// Bornes serveur : { quantite, max_credit } — max_credit = ce qui a été
	// débité des lots, donc re-créditable (au-delà = vraie commande).
	const [info, setInfo] = useState(null)
	const [quantite, setQuantite] = useState(String(article.quantite))
	const [type, setType] = useState("")
	const [motif, setMotif] = useState("")
	const [confirm, setConfirm] = useState(false)

	useEffect(() => {
		let annule = false
		fetch(`${URL}api/inventaire/corrections?article=${article.id}`, {
			headers: { Authorization: `Bearer ${token}` },
		})
			.then((res) => (res.ok ? res.json() : null))
			.then((json) => {
				if (!annule && json) setInfo(json)
			})
			.catch(() => {})
		return () => {
			annule = true
		}
	}, [article.id, token])

	const busy = postState.saving || postState.saved

	const cible = parseFloat(quantite)
	const delta = isNaN(cible) ? 0 : cible - article.quantite
	const sens = delta < -1e-9 ? -1 : delta > 1e-9 ? 1 : 0
	const maxCible = info !== null ? article.quantite + info.max_credit : null

	const changeQuantite = (value) => {
		setConfirm(false)
		if (
			value !== "" &&
			maxCible !== null &&
			parseFloat(value) > maxCible
		) {
			value = String(maxCible)
		}
		setQuantite(value)
		// Un type incompatible avec le nouveau sens est désélectionné (une
		// perte n'augmente pas le stock, une récupération ne le baisse pas).
		const v = parseFloat(value)
		const s = isNaN(v) ? 0 : v - article.quantite
		if (type === "perte" && s > 1e-9) setType("")
		if (type === "recuperation" && s < -1e-9) setType("")
	}

	const typeCompatible = (t) => t.sens === 0 || sens === 0 || t.sens === sens

	const invalide =
		isNaN(cible) ||
		cible < 0 ||
		sens === 0 ||
		type === "" ||
		motif.trim() === ""

	const corriger = () => {
		if (!confirm) {
			setConfirm(true)
			return
		}
		onCorriger({ quantite: cible, type, motif: motif.trim() })
	}

	const reservee = article.quantite_reservee || 0
	const errorMessage =
		postState.error && (postState.error.message || postState.error.msg)

	return (
		<CustomModal
			title="Corriger le stock"
			subtitle={`${article.categorieNom} — ${article.nom}`}
			onClose={onClose}
			maxWidth={520}
			showCloseButton={!busy}
			showFooter={!busy}
			buttons={[
				{ title: "Annuler", styleName: "secondary", onClick: onClose },
				{
					title: confirm ? "Confirmer ?" : "Corriger",
					styleName: "primary",
					onClick: corriger,
					disabled: invalide,
				},
			]}
		>
			{busy ? (
				<div style={s.feedback}>
					{postState.saving ? (
						<FontAwesomeIcon icon={faSync} spin size="2x" />
					) : (
						<FontAwesomeIcon
							icon={faCheck}
							size="2x"
							style={{ color: COLORS.success }}
						/>
					)}
				</div>
			) : (
				<>
					<div style={s.hint}>
						La correction ajuste les lots existants (perte : les plus
						anciens d'abord ; récupération : le dernier entamé) — elle
						est tracée dans les utilisations et notifiée.
					</div>

					<div style={s.ligne}>
						<span style={s.label}>Quantité actuelle</span>
						<span style={s.valeur}>
							{article.quantite}
							{reservee > 0 && (
								<span style={s.reserve}>
									{" "}
									(dont {reservee} réservé)
								</span>
							)}
						</span>
					</div>

					<div style={s.ligne}>
						<span style={s.label}>Nouvelle quantité</span>
						<div style={s.qteBloc}>
							<input
								type="number"
								min={0}
								style={s.input}
								value={quantite}
								autoFocus
								onFocus={(e) => e.target.select()}
								onChange={(e) => changeQuantite(e.target.value)}
							/>
							{sens !== 0 && (
								<span
									style={{
										...s.delta,
										color:
											sens < 0
												? COLORS.danger
												: COLORS.success,
									}}
								>
									{sens > 0 ? "+" : ""}
									{fmt(delta)}
								</span>
							)}
						</div>
					</div>
					{maxCible !== null && info.max_credit > 0 && (
						<div style={s.sousHint}>
							Crédit possible jusqu'à {fmt(maxCible)} (ce qui a été
							débité des lots) — au-delà, passer par une commande.
						</div>
					)}

					<div style={s.ligne}>
						<span style={s.label}>Type</span>
						<div style={s.pills}>
							{TYPES.map((t) => {
								const compatible = typeCompatible(t)
								return (
									<button
										key={t.key}
										type="button"
										disabled={!compatible}
										style={{
											...s.pill(type === t.key),
											...(compatible
												? {}
												: s.pillDisabled),
										}}
										onClick={() => {
											setConfirm(false)
											setType(t.key)
										}}
									>
										{t.label}
									</button>
								)
							})}
						</div>
					</div>

					<div style={s.ligne}>
						<span style={s.label}>Motif</span>
						<input
							type="text"
							style={{ ...s.input, flex: 1 }}
							placeholder="Obligatoire — ex. bidon renversé"
							value={motif}
							onChange={(e) => {
								setConfirm(false)
								setMotif(e.target.value)
							}}
						/>
					</div>

					{sens < 0 && reservee > 0 && (
						<div style={s.warning}>
							{reservee} réservé par des fiches ouvertes : leur
							clôture restera bloquée tant que le stock ne couvre
							pas leur besoin.
						</div>
					)}

					{errorMessage && <div style={s.erreur}>{errorMessage}</div>}
				</>
			)}
		</CustomModal>
	)
}

const s = {
	feedback: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		padding: "24px 0",
		color: COLORS.textMuted,
	},
	hint: {
		fontSize: 13,
		color: COLORS.textMuted,
		padding: "2px 0 14px",
	},
	sousHint: {
		fontSize: 12,
		color: COLORS.textMuted,
		margin: "-6px 0 12px 150px",
	},
	ligne: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		marginBottom: 12,
	},
	label: {
		flex: "0 0 140px",
		fontSize: 13,
		color: COLORS.textMuted,
	},
	valeur: {
		fontSize: 14,
		fontWeight: 600,
		fontVariantNumeric: "tabular-nums",
	},
	reserve: {
		fontWeight: 400,
		color: COLORS.textMuted,
	},
	qteBloc: {
		display: "flex",
		alignItems: "center",
		gap: 10,
	},
	delta: {
		fontSize: 14,
		fontWeight: 700,
		fontVariantNumeric: "tabular-nums",
	},
	input: {
		height: 36,
		padding: "0 10px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 13,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		boxSizing: "border-box",
		width: 110,
	},
	pills: { display: "flex", gap: 6, flexWrap: "wrap" },
	pill: (active) => ({
		padding: "4px 12px",
		borderRadius: 99,
		border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
		backgroundColor: active ? COLORS.accentLight : COLORS.surface,
		color: active ? COLORS.accent : COLORS.textMuted,
		fontSize: 12,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
	}),
	pillDisabled: {
		opacity: 0.4,
		cursor: "not-allowed",
	},
	warning: {
		fontSize: 13,
		color: COLORS.warning,
		fontWeight: 500,
		marginBottom: 10,
	},
	erreur: {
		fontSize: 13,
		color: COLORS.danger,
		fontWeight: 500,
		marginBottom: 4,
	},
}

export default CorrectionModalV2
